import {
	useNavigate,
	useSearchParams,
	useFetcher,
	useLocation,
} from '@remix-run/react'
import { cellToParent, cellToLatLng, latLngToCell, isValidCell } from 'h3-js'
import { Loader2 } from 'lucide-react'
import 'mapbox-gl/dist/mapbox-gl.css'
import posthog from 'posthog-js'
import { useCallback, useEffect, useState } from 'react'
import {
	GeolocateControl,
	Map as MapboxMap,
	NavigationControl,
	type MapLayerMouseEvent,
	type MapRef,
	type ViewState,
	type ViewStateChangeEvent,
} from 'react-map-gl'
import { useSpinDelay } from 'spin-delay'
import { useWindowSize } from 'usehooks-ts'
import {
	LARGE_CLUSTER_RESOLUTION,
	LARGE_CLUSTER_ZOOM_THRESHOLD,
	LARGEST_CLUSTER_RESOLUTION,
	MEDIUM_CLUSTER_RESOLUTION,
	MEDIUM_CLUSTER_ZOOM_THRESHOLD,
	PLOT_RESOLUTION,
	PLOT_ZOOM_THRESHOLD,
	SMALL_CLUSTER_RESOLUTION,
	SMALL_CLUSTER_ZOOM_THRESHOLD,
} from '#app/consts'
import { useFetchPlots } from '#app/hooks/use-fetch-plots'
import { useStoreContext } from '#app/store'
import { Clusters } from './clusters'
import { PlacesSearch } from './places-search'
import { PlotHovered } from './plot-hovered'
import { PlotsFetched } from './plots-fetched'
import { PlotsOwned } from './plots-owned'
import { PlotsSelected } from './plots-selected'
import { Popup, type PopupContent } from './popup'

export const Map = ({
	mapRef,
	mapboxAccessToken,
	showSelectionPrompt = true,
}: {
	mapRef: React.RefObject<MapRef>
	mapboxAccessToken: string
	showSelectionPrompt?: boolean
}) => {
	const debugMode = useStoreContext(s => s.debugMode)
	const [searchParams] = useSearchParams()
	const screenshot = searchParams.get('screenshot')
	const location = useLocation()
	const routeClaimId =
		location.pathname === '/claim' ? searchParams.get('plot') : null
	const [claimPlotId, setClaimPlotId] = useState<string | null>(
		routeClaimId && isValidCell(routeClaimId) ? routeClaimId : null,
	)
	useEffect(() => {
		if (routeClaimId && isValidCell(routeClaimId)) setClaimPlotId(routeClaimId)
		else if (
			(location.pathname === '/' && location.state?.clearClaimSelection) ||
			location.pathname === '/claim/saved' ||
			location.pathname.startsWith('/plot/')
		)
			setClaimPlotId(null)
	}, [routeClaimId, location.pathname, location.state, location.key])

	const setIsMapLoaded = useStoreContext(s => s.setIsMapLoaded)
	const initialViewState = useStoreContext(s => s.viewState)

	const getLoadedPlot = useStoreContext(s => s.getLoadedPlot)
	const getSelectedPlotId = useStoreContext(s => s.getSelectedPlotId)
	const addSelectedPlotId = useStoreContext(s => s.addSelectedPlotId)
	const removeSelectedPlotId = useStoreContext(s => s.removeSelectedPlotId)
	const loadedPlots = useStoreContext(s => s.loadedPlots)
	const selectedPlotIds = useStoreContext(s => s.selectedPlotIds)
	const largeClusterPlotIds = useStoreContext(s => s.largeClusterPlotIds)
	const largestClusterPlotIds = useStoreContext(s => s.largestClusterPlotIds)

	const largeClusterFetcher = useFetcher<{
		largeClusterPlotIds: string[]
	}>({
		key: 'large-cluster-plots',
	})

	const hasSucceeded = !!largeClusterFetcher.data?.largeClusterPlotIds
	const showLoadingOverlay =
		!hasSucceeded &&
		largeClusterPlotIds.length === 0 &&
		largestClusterPlotIds.length === 0

	const delayedShowLoading = useSpinDelay(showLoadingOverlay, {
		delay: 1500,
		minDuration: 300,
	})

	const fetchPlots = useFetchPlots()

	const navigate = useNavigate()

	const [viewState, setViewState] = useState<ViewState>(initialViewState)

	const [popupContent, setPopupContent] = useState<PopupContent | null>(null)

	const [activePlace, setActivePlace] = useState<PopupContent | null>(null)
	useEffect(() => {
		if (location.pathname !== '/') setActivePlace(null)
	}, [location.pathname])

	const showPlots = viewState.zoom >= PLOT_ZOOM_THRESHOLD

	const [hoveredPlot, setHoveredPlot] = useState<string>()

	const onMove = useCallback(
		(e: ViewStateChangeEvent) => {
			setViewState({
				...e.viewState,
				padding: initialViewState.padding,
			})
		},
		[initialViewState.padding],
	)

	const onMoveEnd = useCallback(
		(e: ViewStateChangeEvent) => {
			if (e.viewState.zoom >= PLOT_ZOOM_THRESHOLD - 1) {
				const center = latLngToCell(
					e.viewState.latitude,
					e.viewState.longitude,
					LARGE_CLUSTER_RESOLUTION,
				)
				fetchPlots(center)
			}
		},
		[fetchPlots],
	)

	const onMouseMove = useCallback(
		(e: MapLayerMouseEvent) => {
			if (!showPlots) {
				const features = e.target.queryRenderedFeatures(e.point)
				const clusterFeature = features.find(
					f =>
						f.layer.id.endsWith('-circle-layer') ||
						f.layer.id.endsWith('-selected-circle-layer'),
				)

				if (clusterFeature) {
					const layerId = clusterFeature.layer.id
					const isSelected = layerId.endsWith('-selected-circle-layer')
					const clusterId = clusterFeature.properties?.clusterId as string

					let resolution = SMALL_CLUSTER_RESOLUTION
					if (viewState.zoom < LARGE_CLUSTER_ZOOM_THRESHOLD) {
						resolution = LARGEST_CLUSTER_RESOLUTION
					} else if (viewState.zoom < MEDIUM_CLUSTER_ZOOM_THRESHOLD) {
						resolution = LARGE_CLUSTER_RESOLUTION
					} else if (viewState.zoom < SMALL_CLUSTER_ZOOM_THRESHOLD) {
						resolution = MEDIUM_CLUSTER_RESOLUTION
					}

					let count = 0
					if (isSelected) {
						count = selectedPlotIds.filter(
							id => cellToParent(id, resolution) === clusterId,
						).length
					} else {
						count = loadedPlots.filter(
							plot => cellToParent(plot.plotId, resolution) === clusterId,
						).length
					}

					e.target.getCanvas().style.cursor = 'pointer'
					setPopupContent({
						latitude: e.lngLat.lat,
						longitude: e.lngLat.lng,
						isCluster: true,
						clusterCount: count,
						isClusterSelected: isSelected,
						handle: '',
						note: '',
						user: {
							handle: '',
							id: '',
							name: '',
							email: '',
							createdAt: new Date(),
						} as any,
					})
				} else {
					e.target.getCanvas().style.cursor = 'grab'
					setPopupContent(null)
				}
				return
			}

			const plotId = latLngToCell(e.lngLat.lat, e.lngLat.lng, PLOT_RESOLUTION)
			const fetchedPlot = getLoadedPlot(plotId)

			if (fetchedPlot) {
				e.target.getCanvas().style.cursor = 'pointer'
				setHoveredPlot(undefined)
				const content = fetchedPlot.group
					? {
							latitude: e.lngLat.lat,
							longitude: e.lngLat.lng,
							handle: fetchedPlot.group.handle,
							note: fetchedPlot.group.note ?? '',
							user: fetchedPlot.user,
						}
					: {
							latitude: e.lngLat.lat,
							longitude: e.lngLat.lng,
							handle: fetchedPlot.handle,
							note: fetchedPlot.note ?? '',
							user: fetchedPlot.user,
						}
				setPopupContent(content)
				return
			} else {
				setPopupContent(null)
			}

			if (getSelectedPlotId(plotId)) {
				e.target.getCanvas().style.cursor = 'pointer'
				setHoveredPlot(undefined)
			}

			if (!getSelectedPlotId(plotId)) {
				e.target.getCanvas().style.cursor = 'grab'
				setHoveredPlot(plotId)
			}
		},
		[
			showPlots,
			getLoadedPlot,
			getSelectedPlotId,
			viewState.zoom,
			selectedPlotIds,
			loadedPlots,
		],
	)

	const onMouseLeave = useCallback(() => {
		setPopupContent(null)
	}, [])

	const onMapClick = useCallback(
		(e: MapLayerMouseEvent) => {
			if (!showPlots) return

			const plotId = latLngToCell(e.lngLat.lat, e.lngLat.lng, PLOT_RESOLUTION)
			const plot = getLoadedPlot(plotId)
			if (plot) {
				const [latitude, longitude] = cellToLatLng(plotId)
				const place = plot.group ?? plot
				setActivePlace({
					latitude,
					longitude,
					handle: place.handle,
					note: place.note,
					user: plot.user,
				})
				setPopupContent(null)
				setHoveredPlot(undefined)
				if (location.pathname !== '/' || !searchParams.has('map'))
					navigate('/?map=1')
				posthog.capture('map_plot_clicked', { handle: place.handle })
				return
			}

			setActivePlace(null)
			if (searchParams.get('select') !== 'paid') {
				setClaimPlotId(plotId)
				setHoveredPlot(undefined)
				navigate(`/claim?plot=${plotId}`)
				return
			}
			// Paid mode allows selecting multiple hexes for checkout.
			setClaimPlotId(null)

			if (getSelectedPlotId(plotId)) {
				removeSelectedPlotId(plotId)
				e.target.getCanvas().style.cursor = 'grab'

				posthog.capture('plot_deselected', { plotId: plotId })
			} else {
				addSelectedPlotId(plotId)
				e.target.getCanvas().style.cursor = 'pointer'

				posthog.capture('plot_selected', { plotId: plotId })
			}
		},
		[
			showPlots,
			location.pathname,
			searchParams,
			getLoadedPlot,
			getSelectedPlotId,
			navigate,
			removeSelectedPlotId,
			addSelectedPlotId,
		],
	)

	const windowSize = useWindowSize()

	const padding = screenshot
		? { top: 0, bottom: 0, left: 0, right: 0 }
		: initialViewState.padding

	if (!windowSize.width) return null

	const height = '100%'
	const width = '100%'

	return (
		<>
			<MapboxMap
				ref={mapRef}
				initialViewState={{ ...initialViewState, padding }}
				padding={padding}
				mapStyle="mapbox://styles/chrismoorst/clsip9wqr003901qwc0ibf02q"
				mapboxAccessToken={mapboxAccessToken}
				onMove={onMove}
				onMoveEnd={onMoveEnd}
				onMouseMove={onMouseMove}
				onMouseLeave={onMouseLeave}
				doubleClickZoom={false}
				onClick={onMapClick}
				logoPosition="bottom-left"
				attributionControl={false}
				projection={{
					name: 'globe',
				}}
				onLoad={() => {
					mapRef.current?.resize()
					setIsMapLoaded(true)
				}}
				style={{
					width,
					height,
				}}
				preserveDrawingBuffer={!!screenshot}
			>
				<NavigationControl showCompass={false} position="bottom-right" />
				<GeolocateControl position="bottom-right" />
				{!screenshot && debugMode && <PlotsFetched />}
				{showPlots && (
					<>
						<PlotsOwned />
						<PlotHovered plot={hoveredPlot} />
						<PlotsSelected viewState={viewState} claimPlotId={claimPlotId} />
						<Popup
							popupContent={activePlace ?? popupContent}
							persistent={!!activePlace}
							mobile={windowSize.width < 768}
							onClose={() => {
								setActivePlace(null)
								setPopupContent(null)
							}}
						/>
					</>
				)}
				<Clusters viewState={viewState} claimPlotId={claimPlotId} />
				{/* TODO (low) dev mode clusters for fetched with fetchedAt countdown */}
			</MapboxMap>
			{!screenshot && <PlacesSearch mapRef={mapRef} viewState={viewState} />}
			{!screenshot && showSelectionPrompt && !showPlots && (
				<div className="map-zoom-prompt">
					<p>Zoom in to choose a place</p>
					<button
						onClick={() =>
							mapRef.current?.flyTo({
								zoom: PLOT_ZOOM_THRESHOLD + 0.5,
								duration: window.matchMedia('(prefers-reduced-motion: reduce)')
									.matches
									? 0
									: 900,
							})
						}
					>
						Zoom in <span aria-hidden="true">+</span>
					</button>
				</div>
			)}
			{delayedShowLoading && (
				<div
					role="status"
					className="pointer-events-none absolute right-4 top-20 z-10 flex items-center gap-2 rounded-full border bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-sm"
				>
					<Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
					Loading places…
				</div>
			)}
		</>
	)
}
